/**
 * Consumer trips CRUD.
 * localStorage is the working cache; sync uses Device Sync (/devicesync)
 * so trips match the iOS app (not jsontransfer).
 */

import { API_BASE_URL, getUserSubFromToken } from './apiClient';
import {
  catalogRecordId,
  cityVisitRecordId,
  fetchAllDeviceSyncChanges,
  getDeviceSync,
  putDeviceSync,
  tripRecordId,
  type DeviceSyncRecord,
} from './deviceSyncService';
import {
  applyDeviceSyncRecords,
  buildGranularCityVisitBody,
  decodeCityOption,
  decodeCityVisit,
  decodeItem,
  encodeCityVisit,
  encodeItem,
  hotelStaysToTrip,
  itineraryDaysFromCityVisit,
} from './deviceSync';
import { tripsFingerprintMatch, orphanTripIdsToSkipPush } from '../utils/tripDedup';
import { parseContentTimestamp, stampTripContent } from '../utils/contentTimestamps';
import { taskItemsFromDefaultTemplate } from '../utils/tripTaskTemplate';
import { rewriteLandTripWallClockDates } from '../utils/landWallClockMigration';
import { loadLocalUserPreferences } from './userPreferencesSyncService';
import {
  claimNilOwnerTrip,
  claimNilOwnerTrips,
  getActiveSharedTripIds,
  isAccessibleTrip,
  isOwnedTrip,
  visibleTrips,
} from '../utils/tripAccessPolicy';
import {
  applyOverlaysToTripDraft,
  parseCanonicalTripId,
} from './sharedTripSyncService';
import {
  getCityWireShadow,
  getTripWireShadow,
  mergeCityVisitWireWithWebEdits,
  mergeWireBodies,
  setCityWireShadow,
  setTripWireShadow,
  deleteTripWireShadow,
  deleteCityWireShadow,
} from './deviceSync/wireShadowStore';
import { DeviceSyncRecordType, DEVICE_SYNC_EXPORT_SCHEMA_VERSION } from './deviceSync/types';
import type {
  Trip,
  TripAttachmentRef,
  TripCityOption,
  TripCityVisitMeta,
  TripHotelStay,
  TripTaskItem,
  TripType,
  TripUserPlanSelection,
} from '../types/client';
import { createEmptyTrip, createEmptyTripPreferences } from '../types/client';
import { parseCityVisitMetaFromDevice } from '../utils/tripDayCards';
import {
  isTripUuid,
  resolveTripFromRef,
  tripWebPath,
  type TripUrlFields,
} from '../utils/tripUrl';

const STORAGE_PREFIX = 'wt_user_trips_';

/** Cognito sub for trip localStorage / Device Sync writes (AI plan job persistence). */
export function getUserIdForTripWrites(): string | null {
  return getUserSubFromToken();
}

/**
 * Normalize ids so iOS uppercase UUIDs match web lowercase record keys.
 * Non-UUID strings are returned trimmed as-is (lowercased when UUID-shaped).
 */
export function normalizeEntityId(id: string | null | undefined): string {
  const raw = String(id ?? '').trim();
  if (!raw) return '';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
    return raw.toLowerCase();
  }
  return raw;
}

function remapKeyedByNormalizedId<T>(
  input: Record<string, T> | null | undefined
): Record<string, T> {
  if (!input) return {};
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(input)) {
    const id = normalizeEntityId(key);
    if (!id) continue;
    out[id] = value;
  }
  return out;
}

function getStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function loadTrips(userId: string): Trip[] {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeTrip);
  } catch {
    return [];
  }
}

function saveTripsLocal(userId: string, trips: Trip[]): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(trips));
}

function toDeviceTripType(tripType: TripType | null): string | undefined {
  if (!tripType) return undefined;
  const map: Record<TripType, string> = {
    organized_tour: 'Organized Tour',
    cruise: 'Cruise',
    business: 'Business',
    backpacking: 'Backpacking',
    luxury: 'Luxury',
    adventure: 'Adventure',
    cultural: 'Cultural',
    other: 'Other',
  };
  return map[tripType] ?? tripType;
}

function fromDeviceTripType(raw: unknown): TripType | null {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  const aliases: Record<string, TripType> = {
    organized_tour: 'organized_tour',
    organizedtour: 'organized_tour',
    cruise: 'cruise',
    business: 'business',
    backpacking: 'backpacking',
    luxury: 'luxury',
    adventure: 'adventure',
    cultural: 'cultural',
    other: 'other',
  };
  return aliases[s] ?? (s as TripType);
}

function dateOnly(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function mapCityOptionsFromDevice(raw: unknown): TripCityOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((o) => decodeCityOption(o));
}

/** Parse Device Sync / export `taskItems` — mirrors iOS TripTaskItemExportData. */
export function mapTaskItemsFromDevice(raw: unknown): TripTaskItem[] {
  if (!Array.isArray(raw)) return [];
  const items: TripTaskItem[] = raw.map((o, index) => {
    const item = (o && typeof o === 'object' ? o : {}) as Record<string, unknown>;
    const sortRaw = item.sortOrder;
    const sortOrder =
      typeof sortRaw === 'number' && Number.isFinite(sortRaw) ? sortRaw : index;
    const taskKeyRaw = item.taskKey ?? item.task_key;
    const taskKey =
      taskKeyRaw != null && String(taskKeyRaw).trim()
        ? String(taskKeyRaw).trim()
        : null;
    return {
      id: normalizeEntityId(String(item.id ?? crypto.randomUUID())) || crypto.randomUUID(),
      title: String(item.title ?? '').trim(),
      isDone: Boolean(item.isDone ?? item.completed),
      sortOrder,
      taskKey,
    };
  });
  return items
    .filter((t) => t.title.length > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

/** City display name from a Device Sync city visit body (chunk nested or granular). */
export function extractCityVisitName(body: Record<string, unknown>): string {
  const direct = String(
    body.cityName ?? body.city ?? body.name ?? body.portName ?? ''
  ).trim();
  if (direct) return direct;
  const days = body.days;
  if (Array.isArray(days)) {
    for (const day of days) {
      if (!day || typeof day !== 'object') continue;
      const city = String((day as Record<string, unknown>).city ?? '').trim();
      if (city) return city;
    }
  }
  return '';
}

function cityVisitIdFromRecord(cv: DeviceSyncRecord): string {
  const b = cv.body ?? {};
  const fromBody = normalizeEntityId(String(b.cityVisitId ?? b.id ?? ''));
  if (fromBody) return fromBody;
  const marker = '#cityVisit#';
  const idx = cv.id?.indexOf(marker) ?? -1;
  if (idx >= 0) {
    return normalizeEntityId(cv.id.slice(idx + marker.length));
  }
  return '';
}

/** Merge maps preferring non-empty remote values so blank sync fields do not wipe local names. */
function mergePreferNonEmptyStrings(
  local: Record<string, string> | undefined,
  remote: Record<string, string> | undefined
): Record<string, string> {
  const out: Record<string, string> = { ...(local ?? {}) };
  for (const [key, value] of Object.entries(remote ?? {})) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) {
      out[key] = trimmed;
    } else if (!(key in out)) {
      out[key] = '';
    }
  }
  return out;
}

function tripFromDeviceRecords(
  tripRec: DeviceSyncRecord,
  cityVisits: DeviceSyncRecord[]
): Trip | null {
  const body = tripRec.body ?? {};
  if (body.deleted === true) return null;
  const tripId = normalizeEntityId(String(body.tripId ?? ''));
  if (!tripId) return null;

  const cityVisitIds = Array.isArray(body.cityVisitIds)
    ? body.cityVisitIds.map((id) => normalizeEntityId(String(id))).filter(Boolean)
    : [];
  const cityVisitIdSet = new Set(cityVisitIds);

  const cityNames: Record<string, string> = {};
  const cityOptions: Record<string, TripCityOption[]> = {};
  const cityCoords: Record<string, { latitude: number; longitude: number }> = {};
  const cityVisitMeta: Record<string, TripCityVisitMeta> = {};
  const hotelStaysByCity: Record<string, TripHotelStay[]> = {};
  const itineraryDays: Trip['itineraryDays'] = [];
  for (const cv of cityVisits) {
    const b = cv.body ?? {};
    if (b.deleted === true) continue;
    const cityVisitId = cityVisitIdFromRecord(cv);
    if (!cityVisitId) continue;
    const bodyTripId = normalizeEntityId(String(b.tripId ?? ''));
    // Chunk nested visits omit tripId; match via trip roster or explicit tripId.
    const belongsToTrip =
      (Boolean(bodyTripId) && bodyTripId === tripId) || cityVisitIdSet.has(cityVisitId);
    if (!belongsToTrip) continue;
    const decoded = decodeCityVisit(b);
    const name = extractCityVisitName(b) || decoded.cityName;
    if (name) {
      cityNames[cityVisitId] = name;
    }
    cityOptions[cityVisitId] = mapCityOptionsFromDevice(b.options);
    hotelStaysByCity[cityVisitId] = hotelStaysToTrip(decoded.hotelStays);
    const lat = typeof b.latitude === 'number' ? b.latitude : Number(b.latitude);
    const lng = typeof b.longitude === 'number' ? b.longitude : Number(b.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
      cityCoords[cityVisitId] = { latitude: lat, longitude: lng };
    }
    cityVisitMeta[cityVisitId] = parseCityVisitMetaFromDevice(b);
    for (const day of itineraryDaysFromCityVisit(decoded)) {
      const existing = itineraryDays.find((d) => d.date === day.date && d.cityId === day.cityId);
      if (existing) {
        existing.items = [...existing.items, ...day.items];
      } else {
        itineraryDays.push(day);
      }
    }
  }

  let startDate = dateOnly(body.startDate);
  let endDate = dateOnly(body.endDate);
  // When trip shell dates are missing/collapsed, derive span from city visit arrivals.
  const arrivalDates = cityVisitIds
    .map((id) => cityVisitMeta[id]?.arrivalDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  if (arrivalDates.length > 0) {
    if (!startDate) startDate = arrivalDates[0];
    if (!endDate || endDate === startDate) {
      endDate = arrivalDates[arrivalDates.length - 1];
    }
  }

  const travelers =
    typeof body.numberOfTravelers === 'number' ? body.numberOfTravelers : null;

  const userPlanSelections: TripUserPlanSelection[] = Array.isArray(body.userPlanSelections)
    ? (body.userPlanSelections as Record<string, unknown>[]).map((s) => ({
        id: String(s.id ?? ''),
        tripId: normalizeEntityId(String(s.tripId ?? tripId)),
        cityVisitId: normalizeEntityId(String(s.cityVisitId ?? '')),
        itemTitle: String(s.itemTitle ?? ''),
        normalizedTitle: s.normalizedTitle != null ? String(s.normalizedTitle) : undefined,
        selectedSlot: s.selectedSlot != null ? String(s.selectedSlot) : undefined,
        selectedDate: s.selectedDate != null ? String(s.selectedDate) : undefined,
        itemData: s.itemData,
        createdAt: s.createdAt != null ? String(s.createdAt) : undefined,
      }))
    : [];

  const attachmentRefs: TripAttachmentRef[] = Array.isArray(body.attachmentRefs)
    ? (body.attachmentRefs as Record<string, unknown>[]).map((r) => ({
        attachmentId: String(r.attachmentId ?? ''),
        fileId: String(r.fileId ?? ''),
      }))
    : [];

  // Present array (incl. []) replaces trip-wide; missing/null leaves field unset on remote map.
  const tripItems = Array.isArray(body.tripItems)
    ? (body.tripItems as unknown[]).map((raw) => decodeItem(raw).item)
    : undefined;

  return normalizeTrip({
    id: tripId,
    title: String(body.name ?? ''),
    tripType: fromDeviceTripType(body.tripType),
    info: String(body.tripDescription ?? body.info ?? ''),
    startDate,
    endDate,
    cityIds: cityVisitIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    cityOptions,
    hotelStaysByCity,
    taskItems: mapTaskItemsFromDevice(body.taskItems),
    tripItems,
    homeTimeZone: body.homeTimeZone != null ? String(body.homeTimeZone) : null,
    currency: body.currency != null ? String(body.currency) : null,
    temperatureUnit: body.temperatureUnit != null ? String(body.temperatureUnit) : null,
    distanceUnit: body.distanceUnit != null ? String(body.distanceUnit) : null,
    budget: typeof body.budget === 'number' ? body.budget : null,
    budgetCurrency: body.budgetCurrency != null ? String(body.budgetCurrency) : null,
    ownerId: body.ownerId != null ? String(body.ownerId) : null,
    canEdit: body.canEdit == null ? null : Boolean(body.canEdit),
    userPlanSelections,
    attachmentFileIds: Array.isArray(body.attachmentFileIds)
      ? body.attachmentFileIds.map((id) => String(id))
      : [],
    attachmentRefs,
    previewMapImageFileId:
      body.previewMapImageFileId != null ? String(body.previewMapImageFileId) : null,
    /** Schema 1.3 — copy when present; leave null when absent (never invent "now") */
    contentUpdatedAt: parseContentTimestamp(body.contentUpdatedAt),
    cruiseLine:
      body.cruiseLine != null && String(body.cruiseLine).trim()
        ? String(body.cruiseLine).trim()
        : null,
    shipName:
      body.shipName != null && String(body.shipName).trim()
        ? String(body.shipName).trim()
        : null,
    cruiseType:
      body.cruiseType != null && String(body.cruiseType).trim()
        ? String(body.cruiseType).trim()
        : null,
    groupSize: travelers,
    adults: travelers,
    children: null,
    childrenAges: [],
    preferences: createEmptyTripPreferences(),
    travelCompanions: [],
    itineraryDays,
  });
}

export function buildTripV1Body(
  trip: Trip,
  deleted = false,
  userId?: string | null
): Record<string, unknown> {
  const taskItems = (trip.taskItems ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((t) => ({
      id: t.id,
      title: t.title,
      isDone: t.isDone,
      sortOrder: t.sortOrder,
      ...(t.taskKey ? { taskKey: t.taskKey } : {}),
    }));
  const webBody: Record<string, unknown> = {
    schemaVersion: 1,
    exportSchemaVersion: DEVICE_SYNC_EXPORT_SCHEMA_VERSION,
    tripId: trip.id,
    name: trip.title || 'Untitled trip',
    startDate: trip.startDate ?? undefined,
    endDate: trip.endDate ?? undefined,
    tripType: toDeviceTripType(trip.tripType),
    tripDescription: trip.info || undefined,
    numberOfTravelers: trip.adults ?? trip.groupSize ?? undefined,
    cityVisitIds: trip.cityIds ?? [],
    citySyncMode: 'granular',
    chunkCount: 0,
    deleted,
    taskItems,
  };
  if (trip.homeTimeZone) webBody.homeTimeZone = trip.homeTimeZone;
  if (trip.currency) webBody.currency = trip.currency;
  if (trip.temperatureUnit) webBody.temperatureUnit = trip.temperatureUnit;
  if (trip.distanceUnit) webBody.distanceUnit = trip.distanceUnit;
  if (trip.budget != null) webBody.budget = trip.budget;
  if (trip.budgetCurrency) webBody.budgetCurrency = trip.budgetCurrency;
  // Always export ownership on owned pushes (authoritative contract with iOS).
  webBody.ownerId = trip.ownerId ?? undefined;
  webBody.canEdit = trip.canEdit == null ? true : trip.canEdit;
  if (trip.userPlanSelections?.length) {
    webBody.userPlanSelections = trip.userPlanSelections;
  }
  if (trip.attachmentFileIds?.length) {
    webBody.attachmentFileIds = trip.attachmentFileIds;
  }
  if (trip.attachmentRefs?.length) {
    webBody.attachmentRefs = trip.attachmentRefs;
  }
  if (trip.previewMapImageFileId) {
    webBody.previewMapImageFileId = trip.previewMapImageFileId;
  }
  if (trip.contentUpdatedAt) {
    webBody.contentUpdatedAt = trip.contentUpdatedAt;
  }
  const cruiseLine = trip.cruiseLine?.trim();
  if (cruiseLine) {
    webBody.cruiseLine = cruiseLine;
  }
  const shipName = trip.shipName?.trim();
  if (shipName) {
    webBody.shipName = shipName;
  }
  const cruiseType = trip.cruiseType?.trim();
  if (cruiseType) {
    webBody.cruiseType = cruiseType;
  }
  // Schema 1.4: omit tripItems when undefined (never hydrated); include [] only to clear.
  if (trip.tripItems !== undefined) {
    webBody.tripItems = trip.tripItems.map((item) => encodeItem(item));
  }

  const shadow = userId ? getTripWireShadow(userId, normalizeEntityId(trip.id)) : null;
  const merged = mergeWireBodies(shadow, webBody);
  // Web is granular — keep chunkCount/citySyncMode from web overlay
  merged.citySyncMode = 'granular';
  merged.chunkCount = 0;
  merged.taskItems = taskItems;
  merged.cityVisitIds = trip.cityIds ?? [];
  merged.deleted = deleted;
  if (trip.tripItems !== undefined) {
    merged.tripItems = webBody.tripItems;
  } else {
    delete merged.tripItems;
  }
  return merged;
}

function buildCityVisitBody(
  trip: Trip,
  cityVisitId: string,
  userId?: string | null
): Record<string, unknown> {
  const webBody = buildGranularCityVisitBody(trip, cityVisitId);
  const id = normalizeEntityId(cityVisitId);
  const shadow = userId ? getCityWireShadow(userId, id) : null;
  return mergeCityVisitWireWithWebEdits(shadow, webBody);
}

async function upsertCatalogEntry(sub: string, tripId: string, deleted: boolean): Promise<void> {
  const catalogId = catalogRecordId(sub);
  const normalizedTripId = normalizeEntityId(tripId);
  let trips: Array<Record<string, unknown>> = [];
  try {
    const existing = await getDeviceSync(catalogId);
    const list = existing?.body?.trips;
    if (Array.isArray(list)) {
      trips = list.map((t) => ({ ...(t as Record<string, unknown>) }));
    }
  } catch (err) {
    console.warn('tripsService: catalog get failed', err);
  }

  const now = new Date().toISOString();
  const catalogIndex = trips.findIndex(
    (tripEntry) => normalizeEntityId(String(tripEntry.tripId ?? '')) === normalizedTripId
  );
  const entry = {
    tripId: normalizedTripId,
    updatedAt: now,
    deleted,
    syncComplete: true,
  };
  if (catalogIndex >= 0) {
    trips[catalogIndex] = { ...trips[catalogIndex], ...entry };
    trips = trips.filter(
      (tripEntry, index) =>
        index === catalogIndex ||
        normalizeEntityId(String(tripEntry.tripId ?? '')) !== normalizedTripId
    );
  } else {
    trips.push(entry);
  }

  await putDeviceSync(
    catalogId,
    'sync-catalog-v1',
    {
      schemaVersion: 1,
      trips,
    },
    sub
  );
}

/**
 * Push one trip (+ city visits + catalog entry) to Device Sync.
 * Prefer passing `userId` (Auth `sub`) so import/create does not depend on token parse timing.
 */
export async function syncTripToDevice(
  trip: Trip,
  options: {
    deleted?: boolean;
    userId?: string | null;
    /** City visit ids removed since last sync — push deleted:true tombstones. */
    removedCityVisitIds?: string[];
  } = {}
): Promise<void> {
  const deleted = Boolean(options.deleted);
  const sub = options.userId || getUserSubFromToken();
  if (!sub) {
    console.warn('tripsService: no Cognito sub; skip device sync push');
    return;
  }

  let toPush = trip;
  const owner = trip.ownerId != null ? String(trip.ownerId).trim() : '';
  if (owner && owner !== sub) {
    console.warn(
      'tripsService: skip device sync push for foreign owner',
      trip.id,
      'ownerId=',
      owner
    );
    return;
  }
  if (!owner) {
    toPush = claimNilOwnerTrip(trip, sub);
    // Persist claim so local cache matches what we push.
    const trips = loadTrips(sub);
    const idx = trips.findIndex((t) => normalizeEntityId(t.id) === normalizeEntityId(trip.id));
    if (idx >= 0) {
      trips[idx] = { ...trips[idx], ownerId: sub, canEdit: true };
      saveTripsLocal(sub, trips);
    }
  }

  console.info(
    `tripsService: device sync ${deleted ? 'tombstone' : 'push'} trip=${toPush.id} cities=${toPush.cityIds?.length ?? 0}`
  );

  const tripBody = buildTripV1Body(toPush, deleted, sub);
  const normalizedTripId = normalizeEntityId(toPush.id);
  await putDeviceSync(tripRecordId(sub, normalizedTripId), 'trip-v1', tripBody, sub);
  if (!deleted) {
    setTripWireShadow(sub, normalizedTripId, tripBody);
  }

  if (!deleted) {
    for (const cityVisitId of toPush.cityIds ?? []) {
      const body = buildCityVisitBody(toPush, cityVisitId, sub);
      const normalizedCityVisitId = normalizeEntityId(cityVisitId);
      await putDeviceSync(
        cityVisitRecordId(sub, normalizedCityVisitId),
        'cityVisit-v1',
        body,
        sub
      );
      setCityWireShadow(sub, normalizedCityVisitId, body);
    }
    for (const removedId of options.removedCityVisitIds ?? []) {
      if (!removedId) continue;
      const normalizedRemovedId = normalizeEntityId(removedId);
      await putDeviceSync(
        cityVisitRecordId(sub, normalizedRemovedId),
        'cityVisit-v1',
        {
          schemaVersion: 1,
          exportSchemaVersion: DEVICE_SYNC_EXPORT_SCHEMA_VERSION,
          tripId: normalizedTripId,
          cityVisitId: normalizedRemovedId,
          deleted: true,
        },
        sub
      );
    }
  }

  await upsertCatalogEntry(sub, normalizedTripId, deleted);
}

export function mergeRemoteOverLocal(local: Trip[], remote: Trip[]): Trip[] {
  const byId = new Map<string, Trip>();
  const remoteList = [...remote];
  for (const t of remote) byId.set(normalizeEntityId(t.id), t);
  for (const t of local) {
    const localId = normalizeEntityId(t.id);
    const remoteTrip = byId.get(localId);
    if (!remoteTrip) {
      // Drop local-only trips that fingerprint-match a remote trip (re-import orphans).
      const remoteMatch = remoteList.find((r) =>
        tripsFingerprintMatch(
          {
            title: t.title,
            tripType: t.tripType,
            startDate: t.startDate,
            endDate: t.endDate,
            sourceItineraryId: t.sourceItineraryId,
          },
          {
            title: r.title,
            tripType: r.tripType,
            startDate: r.startDate,
            endDate: r.endDate,
            sourceItineraryId: r.sourceItineraryId,
          }
        )
      );
      if (remoteMatch) {
        console.warn(
          'tripsService: dropping local duplicate trip',
          localId,
          'preferring remote',
          normalizeEntityId(remoteMatch.id)
        );
        continue;
      }
      byId.set(localId, t);
      continue;
    }
    // Prefer remote shell; keep richer local planning fields when remote has none
    byId.set(localId, {
      ...remoteTrip,
      id: localId,
      sourceItineraryId:
        remoteTrip.sourceItineraryId ?? t.sourceItineraryId ?? null,
      cruiseLine: remoteTrip.cruiseLine?.trim()
        ? remoteTrip.cruiseLine.trim()
        : t.cruiseLine ?? null,
      shipName: remoteTrip.shipName?.trim()
        ? remoteTrip.shipName.trim()
        : t.shipName ?? null,
      cruiseType: remoteTrip.cruiseType?.trim()
        ? remoteTrip.cruiseType.trim()
        : t.cruiseType ?? null,
      cityNames: mergePreferNonEmptyStrings(
        remapKeyedByNormalizedId(t.cityNames),
        remapKeyedByNormalizedId(remoteTrip.cityNames)
      ),
      cityCoords: {
        ...remapKeyedByNormalizedId(t.cityCoords),
        ...remapKeyedByNormalizedId(remoteTrip.cityCoords),
      },
      cityVisitMeta: {
        ...remapKeyedByNormalizedId(t.cityVisitMeta),
        ...remapKeyedByNormalizedId(remoteTrip.cityVisitMeta),
      },
      cityOptions: {
        ...remapKeyedByNormalizedId(t.cityOptions),
        ...remapKeyedByNormalizedId(remoteTrip.cityOptions),
      },
      hotelStaysByCity: {
        ...remapKeyedByNormalizedId(t.hotelStaysByCity),
        ...remapKeyedByNormalizedId(remoteTrip.hotelStaysByCity),
      },
      aiPlanDataByCity: {
        ...remapKeyedByNormalizedId(t.aiPlanDataByCity),
        ...remapKeyedByNormalizedId(remoteTrip.aiPlanDataByCity),
      },
      taskItems:
        (remoteTrip.taskItems?.length ?? 0) > 0
          ? remoteTrip.taskItems
          : t.taskItems ?? [],
      userPlanSelections:
        (remoteTrip.userPlanSelections?.length ?? 0) > 0
          ? remoteTrip.userPlanSelections
          : t.userPlanSelections ?? [],
      itineraryDays:
        (remoteTrip.itineraryDays?.length ?? 0) > 0
          ? remoteTrip.itineraryDays
          : t.itineraryDays,
      cityHistories: {
        ...remapKeyedByNormalizedId(t.cityHistories),
        ...remapKeyedByNormalizedId(remoteTrip.cityHistories),
      },
      homeTimeZone: remoteTrip.homeTimeZone ?? t.homeTimeZone ?? null,
      currency: remoteTrip.currency ?? t.currency ?? null,
      temperatureUnit: remoteTrip.temperatureUnit ?? t.temperatureUnit ?? null,
      distanceUnit: remoteTrip.distanceUnit ?? t.distanceUnit ?? null,
      budget: remoteTrip.budget ?? t.budget ?? null,
      budgetCurrency: remoteTrip.budgetCurrency ?? t.budgetCurrency ?? null,
      ownerId: remoteTrip.ownerId ?? t.ownerId ?? null,
      canEdit: remoteTrip.canEdit ?? t.canEdit ?? null,
      attachmentFileIds:
        (remoteTrip.attachmentFileIds?.length ?? 0) > 0
          ? remoteTrip.attachmentFileIds
          : t.attachmentFileIds ?? [],
      attachmentRefs:
        (remoteTrip.attachmentRefs?.length ?? 0) > 0
          ? remoteTrip.attachmentRefs
          : t.attachmentRefs ?? [],
      // Present tripItems (incl. []) replaces; missing key preserves local trip-wide.
      tripItems:
        remoteTrip.tripItems !== undefined ? remoteTrip.tripItems : t.tripItems,
      previewMapImageFileId:
        remoteTrip.previewMapImageFileId ?? t.previewMapImageFileId ?? null,
      // Prefer remote content stamp when present; keep local when remote omits (old schema)
      contentUpdatedAt:
        remoteTrip.contentUpdatedAt ?? t.contentUpdatedAt ?? null,
    });
  }
  return Array.from(byId.values());
}

export function normalizeTrip(raw: unknown): Trip {
  const empty = createEmptyTrip();
  const t = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const cityOptionsRaw = t.cityOptions;
  let cityOptions: Record<string, TripCityOption[]> = {};
  if (cityOptionsRaw && typeof cityOptionsRaw === 'object' && !Array.isArray(cityOptionsRaw)) {
    cityOptions = Object.fromEntries(
      Object.entries(cityOptionsRaw as Record<string, unknown>).map(([cityId, opts]) => [
        normalizeEntityId(cityId),
        Array.isArray(opts)
          ? opts.map((o) => {
              const opt = (o && typeof o === 'object' ? o : {}) as Record<string, unknown>;
              return {
                id: String(opt.id ?? crypto.randomUUID()),
                title: String(opt.title ?? ''),
                description: opt.description != null ? String(opt.description) : undefined,
                url: opt.url != null ? String(opt.url) : undefined,
                completed: Boolean(opt.completed),
                address: opt.address != null ? String(opt.address) : undefined,
                order: typeof opt.order === 'number' ? opt.order : undefined,
              };
            })
          : [],
      ])
    );
  }

  const cityNamesRaw = t.cityNames;
  const cityNames =
    cityNamesRaw && typeof cityNamesRaw === 'object' && !Array.isArray(cityNamesRaw)
      ? Object.fromEntries(
          Object.entries(cityNamesRaw as Record<string, unknown>).map(([k, v]) => [
            normalizeEntityId(k),
            String(v ?? ''),
          ])
        )
      : {};

  const cityCoordsRaw = t.cityCoords;
  const cityCoords: Record<string, { latitude: number; longitude: number }> = {};
  if (cityCoordsRaw && typeof cityCoordsRaw === 'object' && !Array.isArray(cityCoordsRaw)) {
    for (const [id, value] of Object.entries(cityCoordsRaw as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const c = value as Record<string, unknown>;
      const lat = typeof c.latitude === 'number' ? c.latitude : Number(c.latitude);
      const lng = typeof c.longitude === 'number' ? c.longitude : Number(c.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        cityCoords[normalizeEntityId(id)] = { latitude: lat, longitude: lng };
      }
    }
  }

  const cityVisitMetaRaw = t.cityVisitMeta;
  const cityVisitMeta: Record<string, TripCityVisitMeta> = {};
  if (cityVisitMetaRaw && typeof cityVisitMetaRaw === 'object' && !Array.isArray(cityVisitMetaRaw)) {
    for (const [id, value] of Object.entries(cityVisitMetaRaw as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      cityVisitMeta[normalizeEntityId(id)] = parseCityVisitMetaFromDevice(
        value as Record<string, unknown>
      );
    }
  }

  const cityHistoriesRaw = t.cityHistories;
  const cityHistories =
    cityHistoriesRaw && typeof cityHistoriesRaw === 'object' && !Array.isArray(cityHistoriesRaw)
      ? remapKeyedByNormalizedId(cityHistoriesRaw as Trip['cityHistories'])
      : {};

  const cityIds = Array.isArray(t.cityIds)
    ? t.cityIds.map((id) => normalizeEntityId(String(id))).filter(Boolean)
    : [];

  return {
    id: normalizeEntityId(String(t.id ?? crypto.randomUUID())) || crypto.randomUUID(),
    title: String(t.title ?? empty.title),
    tripType: (t.tripType as TripType | null) ?? null,
    info: String(t.info ?? ''),
    startDate: t.startDate != null ? String(t.startDate) : null,
    endDate: t.endDate != null ? String(t.endDate) : null,
    cityIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    cityHistories,
    cityOptions,
    groupSize: typeof t.groupSize === 'number' ? t.groupSize : null,
    adults: typeof t.adults === 'number' ? t.adults : null,
    children: typeof t.children === 'number' ? t.children : null,
    childrenAges: Array.isArray(t.childrenAges)
      ? t.childrenAges.map((n) => Number(n)).filter((n) => !Number.isNaN(n))
      : [],
    preferences:
      t.preferences && typeof t.preferences === 'object'
        ? { ...createEmptyTripPreferences(), ...(t.preferences as object) }
        : createEmptyTripPreferences(),
    travelCompanions: Array.isArray(t.travelCompanions) ? (t.travelCompanions as Trip['travelCompanions']) : [],
    itineraryDays: Array.isArray(t.itineraryDays) ? (t.itineraryDays as Trip['itineraryDays']) : [],
    taskItems: mapTaskItemsFromDevice(t.taskItems),
    hotelStaysByCity: remapKeyedByNormalizedId(
      (t.hotelStaysByCity as Trip['hotelStaysByCity']) ?? {}
    ),
    aiPlanDataByCity: remapKeyedByNormalizedId(
      (t.aiPlanDataByCity as Trip['aiPlanDataByCity']) ?? {}
    ),
    homeTimeZone: t.homeTimeZone != null ? String(t.homeTimeZone) : null,
    currency: t.currency != null ? String(t.currency) : null,
    temperatureUnit: t.temperatureUnit != null ? String(t.temperatureUnit) : null,
    distanceUnit: t.distanceUnit != null ? String(t.distanceUnit) : null,
    budget: typeof t.budget === 'number' ? t.budget : null,
    budgetCurrency: t.budgetCurrency != null ? String(t.budgetCurrency) : null,
    ownerId: t.ownerId != null ? String(t.ownerId) : null,
    canEdit: t.canEdit == null ? null : Boolean(t.canEdit),
    canonicalTripId:
      t.canonicalTripId != null && String(t.canonicalTripId).trim()
        ? String(t.canonicalTripId).trim()
        : null,
    accessRole:
      t.accessRole != null && String(t.accessRole).trim()
        ? String(t.accessRole).trim()
        : null,
    userPlanSelections: Array.isArray(t.userPlanSelections)
      ? (t.userPlanSelections as TripUserPlanSelection[])
      : [],
    attachmentFileIds: Array.isArray(t.attachmentFileIds)
      ? t.attachmentFileIds.map((id) => String(id))
      : [],
    attachmentRefs: Array.isArray(t.attachmentRefs)
      ? (t.attachmentRefs as TripAttachmentRef[])
      : [],
    tripItems: Array.isArray(t.tripItems)
      ? (t.tripItems as Trip['tripItems'])
      : undefined,
    previewMapImageFileId:
      t.previewMapImageFileId != null ? String(t.previewMapImageFileId) : null,
    sourceItineraryId:
      t.sourceItineraryId != null && String(t.sourceItineraryId).trim()
        ? String(t.sourceItineraryId).trim()
        : null,
    cruiseLine:
      t.cruiseLine != null && String(t.cruiseLine).trim()
        ? String(t.cruiseLine).trim()
        : null,
    shipName:
      t.shipName != null && String(t.shipName).trim()
        ? String(t.shipName).trim()
        : null,
    cruiseType:
      t.cruiseType != null && String(t.cruiseType).trim()
        ? String(t.cruiseType).trim()
        : null,
    contentUpdatedAt: parseContentTimestamp(t.contentUpdatedAt),
  };
}

function countDeviceSyncRecordTypes(records: DeviceSyncRecord[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const record of records) {
    counts[record.type] = (counts[record.type] ?? 0) + 1;
  }
  return counts;
}

/**
 * GET /devicesync catalog when /changes omits it (common — catalog updates less often than chunks).
 * Without a catalog, applyDeviceSyncRecords skips all trip payloads (iOS parity).
 */
async function recordsWithCatalogFallback(
  userId: string,
  records: DeviceSyncRecord[]
): Promise<DeviceSyncRecord[]> {
  const hasCatalog = records.some((r) => r.type === 'sync-catalog-v1');
  if (hasCatalog) return records;
  const catalog = await getDeviceSync(catalogRecordId(userId));
  if (!catalog) return records;
  return [catalog, ...records];
}

/**
 * Pull trips from Device Sync into localStorage using apply LWW (catalog + chunk/granular).
 * Call on login so web shows the same trips as the iOS app.
 */
export async function pullTripsFromBackend(userId: string, _email: string): Promise<void> {
  try {
    // Claim any nil-owner local trips for this user before merge (never re-home others).
    claimNilOwnerTripsForUser(userId);

    const pulled = await fetchAllDeviceSyncChanges();
    const records = await recordsWithCatalogFallback(userId, pulled);
    const typeCounts = countDeviceSyncRecordTypes(records);
    const hadCatalogInChanges = pulled.some((r) => r.type === 'sync-catalog-v1');
    const outcome = applyDeviceSyncRecords(records);

    const incompleteIds = outcome.incompleteCatalogTripIds;
    const syncDiag = {
      userId,
      apiBaseUrl: API_BASE_URL,
      pulledRecords: pulled.length,
      recordsAfterCatalogFallback: records.length,
      hadCatalogInChanges,
      hasCatalogAfterFallback: records.some((r) => r.type === 'sync-catalog-v1'),
      typeCounts,
      appliedTrips: outcome.trips.size,
      incompleteCatalog: incompleteIds.length,
    };
    console.info('tripsService: device sync pull', syncDiag);
    if (incompleteIds.length > 0) {
      console.warn(
        'tripsService: skipping incomplete Device Sync trips (catalog syncComplete=false):',
        incompleteIds
      );
    }

    const { tripRecs, cityVisits } = deviceRecordsForAppliedTrips(
      userId,
      records,
      outcome
    );

    const remoteTrips = tripRecs
      .map((tr) => tripFromDeviceRecords(tr, cityVisits))
      .filter((t): t is Trip => t != null);

    const local = loadTrips(userId);
    const merged = mergeRemoteOverLocal(local, remoteTrips);
    // Claim remaining nils after merge; preserve foreign ownerId from payload.
    const claimed = claimNilOwnerTrips(merged, userId);
    saveTripsLocal(userId, claimed);
  } catch (err) {
    console.warn('tripsService: device sync pull failed', err);
  }
}

/**
 * Map apply outcome + raw records into trip-v1 headers and cityVisit-v1 bodies
 * suitable for `tripFromDeviceRecords` (used by own pull and shared invitee merge).
 */
function deviceRecordsForAppliedTrips(
  userId: string,
  records: DeviceSyncRecord[],
  outcome: ReturnType<typeof applyDeviceSyncRecords>
): { tripRecs: DeviceSyncRecord[]; cityVisits: DeviceSyncRecord[] } {
  const incompleteIds = outcome.incompleteCatalogTripIds;
  const tripRecs: DeviceSyncRecord[] = [];
  const cityVisits: DeviceSyncRecord[] = [];

  for (const record of records) {
    if (record.type === 'trip-v1') {
      const tripId = normalizeEntityId(String(record.body?.tripId ?? ''));
      if (tripId && incompleteIds.includes(tripId)) continue;
      if (outcome.trips.has(tripId) || record.body?.deleted === true) {
        tripRecs.push(record);
      }
    }
  }

  Array.from(outcome.trips.entries()).forEach(([tripId, applied]) => {
    if (!tripRecs.some((r) => normalizeEntityId(String(r.body?.tripId ?? '')) === tripId)) {
      tripRecs.push({
        id: tripRecordId(userId, tripId),
        type: 'trip-v1',
        body: {
          tripId,
          name: applied.name || 'Trip',
          cityVisitIds: Array.from(applied.cityVisits.keys()),
          chunkCount: applied.chunkCount,
          deleted: false,
        },
      });
    }

    setTripWireShadow(userId, tripId, {
      tripId,
      name: applied.name,
      chunkCount: applied.chunkCount,
      cityVisitIds: Array.from(applied.cityVisits.keys()),
    });

    Array.from(applied.cityVisits.entries()).forEach(([cityId, visit]) => {
      const body = encodeCityVisit(visit);
      body.tripId = tripId;
      cityVisits.push({
        id: cityVisitRecordId(userId, cityId),
        type: 'cityVisit-v1',
        body,
      });
      setCityWireShadow(userId, cityId, body);
    });
  });

  for (const tr of records.filter((r) => r.type === 'trip-v1')) {
    const tripId = normalizeEntityId(String(tr.body?.tripId ?? ''));
    if (!tripId || !outcome.trips.has(tripId)) continue;
    if (tr.body?.deleted === true) continue;
    setTripWireShadow(userId, tripId, mergeWireBodies(getTripWireShadow(userId, tripId), tr.body));
    const idx = tripRecs.findIndex(
      (r) => normalizeEntityId(String(r.body?.tripId ?? '')) === tripId
    );
    if (idx >= 0) tripRecs[idx] = tr;
    else tripRecs.push(tr);
  }

  return { tripRecs, cityVisits };
}

/**
 * Persist a shared-trip Device Sync pull into the invitee's local trip store.
 * Uses allowEmptyChunkShell (catalog often omitted). Does not claim ownerId as the invitee.
 * Does not push back to Device Sync (unlike updateTrip).
 */
export function persistSharedTripDeviceSyncRecords(
  userId: string,
  canonicalTripId: string,
  records: DeviceSyncRecord[]
): void {
  const parsed = parseCanonicalTripId(canonicalTripId);
  if (!parsed) return;

  const outcome = applyDeviceSyncRecords(records, { allowEmptyChunkShell: true });
  const { tripRecs, cityVisits } = deviceRecordsForAppliedTrips(userId, records, outcome);

  let remoteTrips = tripRecs
    .map((tr) => tripFromDeviceRecords(tr, cityVisits))
    .filter((t): t is Trip => t != null)
    .filter((t) => normalizeEntityId(t.id) === normalizeEntityId(parsed.tripId));

  // Overlay stamps: author as owner, role/canEdit/canonical from trip-v1 body.
  const changeLike = records.map((r) => ({
    id: r.id,
    type: r.type,
    body: r.body ?? {},
    updatedAt: r.updatedAt ?? null,
  }));

  remoteTrips = remoteTrips.map((trip) => {
    const drafted = applyOverlaysToTripDraft(
      {
        ...trip,
        ownerId: trip.ownerId ?? parsed.authorSub,
      } as Record<string, unknown>,
      changeLike,
      canonicalTripId
    );
    return normalizeTrip({
      ...trip,
      ownerId: (drafted.ownerId as string) ?? parsed.authorSub,
      canEdit:
        drafted.canEdit != null
          ? Boolean(drafted.canEdit)
          : drafted.accessRole === 'viewer'
            ? false
            : trip.canEdit,
      canonicalTripId: (drafted.canonicalTripId as string) ?? canonicalTripId,
      accessRole:
        drafted.accessRole != null ? String(drafted.accessRole) : trip.accessRole ?? null,
    });
  });

  if (remoteTrips.length === 0) return;

  const local = loadTrips(userId);
  // Id upsert only — do not use mergeRemoteOverLocal fingerprint dedup, which can
  // drop an invitee's own trip that happens to share title/dates with the shared one.
  const byId = new Map<string, Trip>();
  for (const t of local) byId.set(normalizeEntityId(t.id), t);
  for (const remote of remoteTrips) {
    const id = normalizeEntityId(remote.id);
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, remote);
      continue;
    }
    byId.set(id, {
      ...remote,
      id,
      // Keep richer local planning if remote shell is thin
      cityNames: mergePreferNonEmptyStrings(
        remapKeyedByNormalizedId(existing.cityNames),
        remapKeyedByNormalizedId(remote.cityNames)
      ),
      cityCoords: {
        ...remapKeyedByNormalizedId(existing.cityCoords),
        ...remapKeyedByNormalizedId(remote.cityCoords),
      },
      cityVisitMeta: {
        ...remapKeyedByNormalizedId(existing.cityVisitMeta),
        ...remapKeyedByNormalizedId(remote.cityVisitMeta),
      },
      itineraryDays:
        (remote.itineraryDays?.length ?? 0) > 0
          ? remote.itineraryDays
          : existing.itineraryDays,
      ownerId: remote.ownerId ?? existing.ownerId ?? null,
      canEdit: remote.canEdit ?? existing.canEdit ?? null,
      canonicalTripId: remote.canonicalTripId ?? existing.canonicalTripId ?? null,
      accessRole: remote.accessRole ?? existing.accessRole ?? null,
    });
  }
  saveTripsLocal(userId, Array.from(byId.values()));
}

/**
 * Assign ownerId = userId on local trips with missing ownerId. Does not change
 * trips that already have a different ownerId.
 */
export function claimNilOwnerTripsForUser(userId: string): void {
  const trips = loadTrips(userId);
  const claimed = claimNilOwnerTrips(trips, userId);
  if (claimed !== trips) {
    saveTripsLocal(userId, claimed);
  }
}

/**
 * Catalog + deleted trip-v1 records only (iOS DeviceSyncService.tripTombstoneRecords).
 * Used for pre-push tombstone apply so remote deletes win without discarding local edits.
 */
export function tripTombstoneRecords(records: DeviceSyncRecord[]): DeviceSyncRecord[] {
  const filtered: DeviceSyncRecord[] = [];
  filtered.push(
    ...records.filter((r) => r.type === DeviceSyncRecordType.catalog)
  );
  filtered.push(
    ...records.filter(
      (r) => r.type === DeviceSyncRecordType.trip && r.body?.deleted === true
    )
  );
  return filtered;
}

/** Trip ids tombstoned via catalog `deleted` or trip-v1 `deleted: true`. */
export function collectTripTombstoneIds(records: DeviceSyncRecord[]): Set<string> {
  const ids = new Set<string>();
  for (const record of records) {
    if (record.type === DeviceSyncRecordType.trip && record.body?.deleted === true) {
      const tripId = normalizeEntityId(String(record.body?.tripId ?? ''));
      if (tripId) ids.add(tripId);
    }
  }
  let latestCatalog: DeviceSyncRecord | null = null;
  let latestMs = Number.NEGATIVE_INFINITY;
  for (const record of records) {
    if (record.type !== DeviceSyncRecordType.catalog) continue;
    const ms = record.updatedAt ? Date.parse(record.updatedAt) : Number.NEGATIVE_INFINITY;
    if (ms >= latestMs) {
      latestCatalog = record;
      latestMs = ms;
    }
  }
  const trips = latestCatalog?.body?.trips;
  if (Array.isArray(trips)) {
    for (const entry of trips) {
      if (!entry || typeof entry !== 'object') continue;
      const o = entry as Record<string, unknown>;
      if (o.deleted !== true) continue;
      const tripId = normalizeEntityId(String(o.tripId ?? ''));
      if (tripId) ids.add(tripId);
    }
  }
  return ids;
}

/**
 * Fetch Device Sync changes and apply catalog / trip-v1 tombstones to local storage
 * without a full merge (iOS pullAndApplyTripTombstonesOnly). Does not advance a
 * sync cursor — web changes feed pulls are full-fetch today.
 */
export async function pullAndApplyTripTombstonesOnly(userId: string): Promise<number> {
  const pulled = await fetchAllDeviceSyncChanges();
  const records = await recordsWithCatalogFallback(userId, pulled);
  const tombstoneOnly = tripTombstoneRecords(records);
  if (tombstoneOnly.length === 0) return 0;

  // Ensure apply sees catalog deleted + trip-v1 deleted (parity with iOS apply).
  applyDeviceSyncRecords(tombstoneOnly);

  const deletedIds = collectTripTombstoneIds(tombstoneOnly);
  if (deletedIds.size === 0) return 0;

  const local = loadTrips(userId);
  const remaining: Trip[] = [];
  let removed = 0;
  for (const trip of local) {
    const id = normalizeEntityId(trip.id);
    if (deletedIds.has(id)) {
      removed += 1;
      deleteTripWireShadow(userId, id);
      for (const cityId of trip.cityIds ?? []) {
        deleteCityWireShadow(userId, normalizeEntityId(cityId));
      }
      continue;
    }
    remaining.push(trip);
  }
  if (removed > 0) {
    saveTripsLocal(userId, remaining);
    console.info(
      'tripsService: applied remote trip tombstones before push',
      { userId, removed, ids: Array.from(deletedIds) }
    );
  }
  return removed;
}

/** Push all local trips to Device Sync (fire-and-forget helper). */
export function pushTripsToBackend(userId: string): void {
  (async () => {
    try {
      // 1) Apply remote trip deletes only so push does not resurrect them.
      await pullAndApplyTripTombstonesOnly(userId);
    } catch (err) {
      console.warn('tripsService: pre-push tombstone pull failed', err);
    }
    claimNilOwnerTripsForUser(userId);
    const trips = loadTrips(userId);
    if (trips.length === 0) return;
    const skipIds = orphanTripIdsToSkipPush(trips);
    for (const trip of trips) {
      if (skipIds.has(trip.id)) {
        console.warn(
          'tripsService: skipping push of fingerprint-duplicate trip',
          trip.id
        );
        continue;
      }
      if (!isOwnedTrip(trip, userId)) {
        console.warn(
          'tripsService: skipping push of non-owned trip',
          trip.id,
          'ownerId=',
          trip.ownerId
        );
        continue;
      }
      try {
        await syncTripToDevice(trip, { userId });
      } catch (err) {
        console.warn('tripsService: device sync push failed for trip', trip.id, err);
      }
    }
  })();
}

function saveTrips(userId: string, trips: Trip[]): void {
  saveTripsLocal(userId, trips);
}

export function getAllTrips(userId: string): Trip[] {
  return loadTrips(userId);
}

/** Trips accessible to the signed-in user (owned + active shares). Claims nils first. */
export function getVisibleTrips(userId: string): Trip[] {
  claimNilOwnerTripsForUser(userId);
  return visibleTrips(loadTrips(userId), userId, getActiveSharedTripIds(userId));
}

export function getTrip(userId: string, tripId: string): Trip | null {
  const id = normalizeEntityId(tripId);
  return loadTrips(userId).find((t) => normalizeEntityId(t.id) === id) ?? null;
}

/** Web-only path like `/trips/alaska-cruise` or `/trips/alaska-cruise/days/2026-09-20`. */
export function tripWebHref(
  userId: string,
  trip: TripUrlFields,
  suffix = ''
): string {
  return tripWebPath(trip, getVisibleTrips(userId), suffix);
}

/** Like getTrip, but returns null when the trip is not accessible to this user. */
export function getAccessibleTrip(userId: string, tripId: string): Trip | null {
  claimNilOwnerTripsForUser(userId);
  const sharedIds = getActiveSharedTripIds(userId);
  let trip = getTrip(userId, tripId);
  if (trip && !isAccessibleTrip(trip, userId, sharedIds)) return null;
  if (!trip) {
    if (isTripUuid(tripId)) return null;
    trip = resolveTripFromRef(visibleTrips(loadTrips(userId), userId, sharedIds), tripId);
  }
  if (!trip) return null;
  if (!isAccessibleTrip(trip, userId, sharedIds)) return null;

  const homeTz = loadLocalUserPreferences().appUserSettings.homeTimeZone?.trim();
  if (!homeTz || trip.tripType === 'cruise') return trip;
  const migrated = rewriteLandTripWallClockDates(trip, homeTz);
  if (migrated === trip) return trip;
  updateTrip(userId, migrated.id, migrated);
  return migrated;
}

export interface CreateTripOptions {
  /**
   * When true, skip the background Device Sync push so the caller can
   * `await syncTripToDevice` (used by cruise import).
   */
  deferDeviceSync?: boolean;
}

export function createTrip(
  userId: string,
  data: Omit<Trip, 'id'>,
  options: CreateTripOptions = {}
): Trip {
  const trips = loadTrips(userId);
  const incomingTasks = data.taskItems ?? [];
  const taskItems =
    incomingTasks.length > 0 ? incomingTasks : taskItemsFromDefaultTemplate();
  const trip = stampTripContent(
    normalizeTrip({
      ...data,
      id: crypto.randomUUID(),
      ownerId: userId,
      canEdit: true,
      taskItems,
    }),
    {
      stampAllDays: Boolean(data.itineraryDays?.length),
    }
  );
  trips.push(trip);
  saveTrips(userId, trips);
  if (!options.deferDeviceSync) {
    syncTripToDevice(trip, { userId }).catch((err) => {
      console.warn('tripsService: create push failed', err);
    });
  }
  return trip;
}

export function updateTrip(
  userId: string,
  tripId: string,
  updates: Partial<Omit<Trip, 'id'>>,
  syncOptions: { removedCityVisitIds?: string[] } = {}
): Trip | null {
  const id = normalizeEntityId(tripId);
  const trips = loadTrips(userId);
  const idx = trips.findIndex((t) => normalizeEntityId(t.id) === id);
  if (idx === -1) return null;
  let next = normalizeTrip({ ...trips[idx], ...updates, id });
  // Local content mutations stamp; pull/merge does not use updateTrip so sync won't re-stamp.
  // If caller explicitly sets contentUpdatedAt (rare), preserve it.
  if (!('contentUpdatedAt' in updates)) {
    next = stampTripContent(next, {
      dayDates: updates.itineraryDays?.map((d) => d.date),
    });
  }
  trips[idx] = next;
  saveTrips(userId, trips);
  syncTripToDevice(trips[idx], {
    userId,
    removedCityVisitIds: syncOptions.removedCityVisitIds,
  }).catch((err) => {
    console.warn('tripsService: update push failed', err);
  });
  return trips[idx];
}

export function deleteTrip(userId: string, tripId: string): boolean {
  const id = normalizeEntityId(tripId);
  const trips = loadTrips(userId);
  const existing = trips.find((t) => normalizeEntityId(t.id) === id);
  const filtered = trips.filter((t) => normalizeEntityId(t.id) !== id);
  if (filtered.length === trips.length) return false;
  saveTrips(userId, filtered);
  if (existing) {
    syncTripToDevice(existing, { deleted: true, userId }).catch((err) => {
      console.warn('tripsService: delete push failed', err);
    });
  }
  return true;
}

export { createEmptyTrip };
